import { Outline, PointType, PointUpperCase, Rect } from '@/partTypes'

import { getPathMinMaxPoint } from './plankCommonFuncs'
import { dealLineSegmentIntersect2 } from './polygonRelation'
import { toUpperCasePoint } from './transform/graph'

/**判断两个矩形是否出现容纳的状态 */
export function isRectangleContained(rect1: Outline, rect2: Outline) {
  // 误差允许
  if (Math.abs(rect2.y2 - rect1.y2) <= 0.05) {
    rect2.y2 = rect1.y2
  }
  // 判断 rect1 是否完全包含 rect2
  const rect1ContainsRect2 =
    compareNumbers(rect1.x1, rect2.x1, '<=') &&
    compareNumbers(rect1.y1, rect2.y1, '<=') &&
    compareNumbers(rect1.x2, rect2.x2, '>=') &&
    compareNumbers(rect1.y2, rect2.y2, '>=')

  // 判断 rect2 是否完全包含 rect1
  const rect2ContainsRect1 =
    compareNumbers(rect2.x1, rect1.x1, '<=') &&
    compareNumbers(rect2.y1, rect1.y1, '<=') &&
    compareNumbers(rect2.x2, rect1.x2, '>=') &&
    compareNumbers(rect2.y2, rect1.y2, '>=')
  return {
    rect1ContainsRect2,
    rect2ContainsRect1,
    // 是否出现容纳
    isContain: rect1ContainsRect2 || rect2ContainsRect1,
  }
}

/** 判断两个矩形是否有可能出现碰撞 */
export function judgeRectIntersect(box1: Outline, box2: Outline) {
  // 结果为真则存在碰撞
  return !(
    box1.x2 <= box2.x1 ||
    box1.x1 >= box2.x2 ||
    box1.y2 <= box2.y1 ||
    box1.y1 >= box2.y2
  )
}

/** 计算一个矩形轮廓的面积 */
export function calcArea(outline: Outline) {
  const { x1, y1, x2, y2 } = outline
  return (x2 - x1) * (y2 - y1)
}

/** 轮廓处理成路径 */
export function rectToPath(rect: Outline) {
  return [
    { x: rect.x1, y: rect.y1 },
    { x: rect.x2, y: rect.y1 },
    { x: rect.x2, y: rect.y2 },
    { x: rect.x1, y: rect.y2 },
  ]
}

/** 获取矩形的相关边界信息以及中心点 */
export function getRectangleBounds(rect: Rect) {
  return {
    top: rect.y,
    bottom: rect.y + rect.height,
    left: rect.x,
    right: rect.x + rect.width,
    verticalCenter: rect.y + rect.height / 2,
    horizontalCenter: rect.x + rect.width / 2,
  }
}

/**
 * 重点
 * 比较两个数字 此函数需要应用在所有关于数字比较运算中
 * @param {number} num1 - 第一个数字
 * @param {number} num2 - 第二个数字
 * @param {string} operator - 比较运算符（'=', '>=', '<='）
 * @param {number} [epsilon=0.00001] - 允许的误差范围
 * @returns {boolean} - 如果比较结果为真返回true，否则返回false
 */
type NumberOperate = '=' | '>' | '<' | '>=' | '<='
export function compareNumbers(
  num1: number,
  num2: number,
  operator: NumberOperate = '=',
  epsilon = 0.001
) {
  const diff = Math.abs(num1 - num2)
  switch (operator) {
    case '=':
      return diff < epsilon
    case '>=':
      return num1 > num2 || diff < epsilon
    case '<=':
      return num1 < num2 || diff < epsilon
    case '>':
      return num1 > num2 && diff >= epsilon
    case '<':
      return num1 < num2 && diff >= epsilon
    default:
      throw new Error('operator params Invalid')
  }
}

/**
 * @description 根据基准点旋转指定角度
 * @param pt 需要旋转的点
 * @param ct 旋转基准点
 * @param angle 角度
 * @returns
 */
export function rotatePoint(pt: PointType, ct: PointType, angle: number) {
  // 将角度转换为弧度
  const radians = (angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const { x, y } = pt
  const { x: cx, y: cy } = ct
  const nx = (x - cx) * cos - (y - cy) * sin + cx
  const ny = (x - cx) * sin + (y - cy) * cos + cy
  return { x: nx, y: ny }
}

/**
 * @description 根据基准点旋转指定角度
 * @param path 路径
 * @param center 旋转基准点
 * @param angle 角度
 * @returns
 */
export function getPathByRotate(
  path: PointType[],
  center: PointType,
  angle: number
) {
  return path?.map((pt) => rotatePoint(pt, center, angle))
}

interface Shape {
  id: string
  shape: Outline
}
export type ScanlineDirection =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'up'
  | 'down'

function normalizeScanlineDirection(
  direction: ScanlineDirection
): 'left' | 'right' | 'top' | 'bottom' {
  if (direction === 'up') return 'top'
  if (direction === 'down') return 'bottom'
  return direction
}

/**
 * 使用扫描线法，在指定的方向上查找可能会发生碰撞的矩形
 * @param targetRect 目标矩形
 * @param others 其他矩形
 * @param direction 方向（推荐 top/bottom/left/right，up/down 兼容旧调用）
 * @returns 可能碰撞的矩形和距离
 */
export function detectPotentialCollisionsScanline(
  targetRect: Outline,
  others: Shape[],
  direction: ScanlineDirection
) {
  const normalizedDirection = normalizeScanlineDirection(direction)
  let minDist = Infinity
  let result: (Shape & { distance: number })[] = []
  for (const s of others) {
    const r = s.shape
    let dist = Infinity
    // 根据不同方向进行计算筛选
    if (normalizedDirection === 'right') {
      if (r.x1 <= targetRect.x2) continue
      if (!(r.y2 > targetRect.y1 && r.y1 < targetRect.y2)) continue
      dist = r.x1 - targetRect.x2
    } else if (normalizedDirection === 'left') {
      if (r.x2 >= targetRect.x1) continue
      if (!(r.y2 > targetRect.y1 && r.y1 < targetRect.y2)) continue
      dist = targetRect.x1 - r.x2
    } else if (normalizedDirection === 'bottom') {
      if (r.y1 <= targetRect.y2) continue
      if (!(r.x2 > targetRect.x1 && r.x1 < targetRect.x2)) continue
      dist = r.y1 - targetRect.y2
    } else if (normalizedDirection === 'top') {
      if (r.y2 >= targetRect.y1) continue
      if (!(r.x2 > targetRect.x1 && r.x1 < targetRect.x2)) continue
      dist = targetRect.y1 - r.y2
    }
    if (dist < minDist) {
      minDist = dist
      result = [{ distance: dist, ...s }]
    } else if (dist === minDist) {
      result.push({ distance: dist, ...s })
    }
  }
  return result
}

/**
 * 判断点离矩形的哪一边最近，并返回该边和最近点
 * @param  path 矩形顶点（按顺时针或逆时针）
 * @param  point 点坐标
 * @returns
 */
export function getClosestRectEdgeWithPoint(
  path: PointType[],
  point: PointType
) {
  const [left, top, right, bottom] = getPathMinMaxPoint(path)
  // 计算点到矩形每一边的距离与最近点
  const distances = {
    top: {
      distance: top - point.y,
      nearestPoint: { x: point.x, y: top },
    },
    bottom: {
      distance: point.y - bottom,
      nearestPoint: { x: point.x, y: bottom },
    },
    left: {
      distance: left - point.x,
      nearestPoint: { x: left, y: point.y },
    },
    right: {
      distance: point.x - right,
      nearestPoint: { x: right, y: point.y },
    },
  }
  return distances
  // // 选出最近的边
  // let minSide: keyof typeof distances = 'top'
  // let minDist = distances.top.distance
  // for (const [side, info] of Object.entries(distances)) {
  //   if (info.distance < minDist) {
  //     minSide = side as keyof typeof distances
  //     minDist = info.distance
  //   }
  // }
  // return {
  //   side: minSide,
  //   distance: minDist,
  //   nearestPoint: distances[minSide].nearestPoint,
  // }
}

/**
 * 严格判断 pathA 是否在 pathB 里面（不包含边界情况）
 * @param pathA 路径 A
 * @param pathB 路径 B
 * @returns true 表示严格包含（pathA 的所有点都在 pathB 内部，不在边界上）
 */
export function isPathStrictlyInside(pathA: PointType[], pathB: PointType[]) {
  const nPathA = toUpperCasePoint(pathA) as PointUpperCase[]
  const nPathB = toUpperCasePoint(pathB) as PointUpperCase[]
  for (const point of nPathA) {
    const result = window.ClipperLib.Clipper.PointInPolygon(point, nPathB)
    // 如果有点在边界上（-1）或在外面（0），则不是严格包含
    if (result !== 1) {
      return false
    }
  }
  return true
}

/**
 * @description 判断 pathA 是否在 pathB 内部（在路径上也算在内部）
 * @param pathA 路径 A
 * @param pathB 路径 B
 * @returns true 表示 pathA 在 pathB 内部（在路径上也算在内部）
 */
export function isPathInside(pathA: PointType[], pathB: PointType[]) {
  //  所有点必须在 polygon 内部或边上
  for (const p of pathA) {
    const r = window.ClipperLib.Clipper.PointInPolygon(p, pathB)
    if (r === 0) return false // 外部
  }
  //  任何线段不能穿出 polygon
  for (let i = 0; i < pathA.length - 1; i++) {
    const p1 = pathA[i]
    const p2 = pathA[i + 1]
    for (let j = 0; j < pathB.length; j++) {
      const q1 = pathB[j]
      const q2 = pathB[(j + 1) % pathB.length]
      // 判断是否真正穿出
      if (dealLineSegmentIntersect2([p1, p2], [q1, q2])) {
        return false
      }
    }
  }

  return true
}

/**
 * @description 通过凸包算法，计算得到多个多边形的整体凸包（最小凸多边形）。
 * @param polygons - 多边形数组，每个多边形是点列表（不要求闭合，只需顶点）
 * @returns 凸包路径，按逆时针顺序排列，且首尾点相同（闭合）。
 *          如果所有顶点共线或不足 3 个不重复点，返回空数组 `[]`。
 */
export function computeConvexHull(polygons: PointType[][]): PointType[] {
  // 1. 收集所有顶点，并去重
  const allPoints: PointType[] = []
  for (const poly of polygons) {
    for (const p of poly) {
      allPoints.push(p)
    }
  }
  const uniqueMap = new Map<string, PointType>()
  for (const p of allPoints) {
    const key = `${p.x},${p.y}`
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, p)
    }
  }
  const points = Array.from(uniqueMap.values())
  // 顶点数少于 3 时无法构成面
  if (points.length < 3) {
    return []
  }
  // 2. 按 x 坐标排序，x 相同按 y 排序
  points.sort((a, b) => a.x - b.x || a.y - b.y)
  /**
   * 计算向量 oa 与 ob 的叉积（oa x ob）
   * 返回值 > 0 表示逆时针，< 0 顺时针，= 0 共线
   */
  const cross = (o: PointType, a: PointType, b: PointType): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  // 3. 构建下凸壳（从左到右，保留逆时针拐点）
  const lower: PointType[] = []
  for (const p of points) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop()
    }
    lower.push(p)
  }
  // 4. 构建上凸壳（从右到左，同样保留逆时针拐点）
  const upper: PointType[] = []
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i]
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop()
    }
    upper.push(p)
  }
  // 5. 合并上下凸壳，去掉首尾重复点
  const hull = lower.slice(0, -1).concat(upper.slice(0, -1))
  // 共线情况下 hull 可能少于 3 个点，返回空
  if (hull.length < 3) {
    return []
  }
  // 6. 返回闭合路径（逆时针，首尾相同）
  return [...hull, window.$copy(hull[0])]
}
